import React from 'react';
import { PostcodeServiceableQuery } from '../../../../apps/SolarContact/SolarContact.types';
import { client } from '../../../../utils/apollo';
import { config } from '../../../../utils/config';
import { Field, Form } from '../../Form.types';
import ManualAddress from '../ManualAddress';
import QASAddressLookUp from '../QASAddressLookUp';
import { QASAddressLookUpObject, QASSearchResult } from '../QASAddressLookUp/QASAddressLookUp.types';
import QualifyingField from '../QualifyingField';
import { AddressFieldProps, AddressFieldState } from './AddressField.types';

class AddressField extends React.Component<AddressFieldProps, AddressFieldState> {
  static getDerivedStateFromProps(
    nextProps: AddressFieldProps,
    prevState: AddressFieldState
  ): Partial<AddressFieldState | null> {
    if (nextProps.showManualAddress !== prevState.showManualAddress) {
      return { showManualAddress: nextProps.showManualAddress };
    } else return null;
  }

  constructor(props: AddressFieldProps) {
    super(props);

    this.state = {
      showManualAddress: props.showManualAddress,
      addressNotSelected: true
    };

    this.getValues = this.getValues.bind(this);
    this.setAddress = this.setAddress.bind(this);
    this.handleOnBlur = this.handleOnBlur.bind(this);
    this.handleAsyncSuccess = this.handleAsyncSuccess.bind(this);
    this.handleAsyncFailure = this.handleAsyncFailure.bind(this);
    this.handleOnAddressSelect = this.handleOnAddressSelect.bind(this);
    this.enterAddressManually = this.enterAddressManually.bind(this);
  }

  componentDidUpdate(nextProps: AddressFieldProps, prevState: AddressFieldState): void {
    const { showManualAddress } = this.state;

    if (nextProps.showManualAddress !== prevState.showManualAddress) {
      this.setState({
        showManualAddress
      });
    }
    if (nextProps.productType != this.props.productType) this.sendQuery();
  }

  getValues = (value: string, productType: string): Partial<PostcodeServiceableQuery> => {
    return {
      options: {
        productType: productType,
        address: {
          postcode: value
        }
      }
    };
  };

  enterAddressManually = (): void => {
    const { form, enterAddressManually, metaData } = this.props;

    form.setFieldValue('address', metaData.form.content.address.manualAddressPlaceholder, true);
    enterAddressManually(true);
  };

  clearAddress = (address: string, field: Field, form: Form): void => {
    form.setFieldValue('address', '', true);
  };

  setAddress = (address: QASAddressLookUpObject, field: Field, form: Form): void => {
    const { unit, streetNumber, streetName, city, state, postcode } = address.qasAddress;
    const { onFieldChange } = this.props;

    if (unit) {
      form.setFieldValue('unit', unit, true);
    } else {
      form.setFieldValue('unit', undefined, true);
    }

    form.setFieldValue('streetNumber', streetNumber, true);
    form.setFieldValue('streetName', streetName, true);
    form.setFieldValue('suburb', city, true);
    form.setFieldValue('state', state, true);
    form.setFieldValue('postcode', postcode, true);
    onFieldChange('postcode', postcode);

    if (address && address.qasSearchResult) {
      form.setFieldValue('address', address, true);
    }
  };

  handleOnBlur = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { form, field } = this.props;
    const { addressNotSelected } = this.state;

    form.setFieldTouched(field.name, true, true);

    if (event.target.value === '' || addressNotSelected) {
      this.clearAddress('', field, form);
    }
  };

  async handleOnChange(address?: QASAddressLookUpObject) {
    if (address && address.qasSearchResult) {
      this.setAddress(address, this.props.field, this.props.form);
      this.sendQuery();
    }
  }

  async sendQuery() {
    const address = this.props.form.values.address;
    if (!address) return;

    const postcode = typeof address === 'string' ? address : address.qasAddress.postcode;

    this.props.setAsyncFetching(true);
    const { data }: any = await client
      .query({
        query: this.props.graphQuery,
        variables: this.getValues(postcode, this.props.productType)
      })
      .catch(() => {
        this.handleAsyncFailure();
      });
    this.handleAsyncSuccess(data);
  }

  handleAsyncSuccess = (data: any): void => {
    const { form, onGraphError, setAsyncFetching, setDisqualification } = this.props;
    const checkType = data.checkServiceability ? data.checkServiceability : data.checkRepairServiceability;

    form.setFieldValue('hiddenPostcode', checkType.serviceable.toString(), true);
    setDisqualification(checkType.serviceable);
    onGraphError(false);
    setAsyncFetching(false);
  };

  handleAsyncFailure = (): void => {
    const { onGraphError, setAsyncFetching } = this.props;

    setAsyncFetching(false);
    onGraphError(true);
  };

  handleOnAddressSelect = (QASSearchResult: QASSearchResult): void => {
    const { form, field, onFieldChange, setAsyncFetching } = this.props;
    const address = QASSearchResult.label;

    this.setState({
      addressNotSelected: false
    });

    setAsyncFetching(true);
    onFieldChange(field.name, address);
    if (QASSearchResult.postcode !== form.values.postcode) {
      form.setFieldValue('hiddenPostcode', 'true', true);
    }
    form.setFieldValue(field.name, address, true);
  };

  render(): JSX.Element {
    const {
      field,
      label,
      form,
      graphQuery,
      metaData,
      spinner,
      productType,
      setAsyncFetching,
      setDisqualification,
      onGraphError,
      graphError,
      graphMessage,
      onFieldChange,
      recordDisqualification,
      disqualificationMessage
    } = this.props;
    const { showManualAddress } = this.state;
    const valueExists = form.values[field.name] !== '';
    const errorExists = form.errors['hiddenPostcode'] !== undefined || form.errors[field.name] !== undefined;

    const disqualify = valueExists && errorExists && !spinner;
    const touched = errorExists && form.touched[field.name];
    const error = disqualify ? ' ' : form.errors[field.name];
    const alertOffset = disqualify ? -3 : 0;
    const errorType = graphError ? graphMessage : error;
    const isValid = showManualAddress ? true : !disqualify;

    return (
      <QualifyingField
        errors={form.errors}
        name={field.name}
        isValid={isValid}
        marginTopOffset={alertOffset}
        disqualificationMessage={disqualificationMessage}
        recordDisqualification={recordDisqualification}
      >
        <QASAddressLookUp
          name={field.name}
          label={label}
          value={form.values[field.name]}
          error={errorType}
          touched={touched}
          disabled={showManualAddress}
          data-lpignore={true}
          qasAuthToken={config.qasAuthToken}
          asyncValidating={spinner}
          allowManualAddressEntry={true}
          manualAddressPlaceholder={metaData.form.content.address.manualAddressPlaceholder}
          showManualAddress={showManualAddress}
          onEnterAddressManually={this.enterAddressManually}
          onChange={address => this.handleOnChange(address)}
          onBlur={e => this.handleOnBlur(e)}
          onAddressSelect={QASSearchResult => this.handleOnAddressSelect(QASSearchResult)}
        >
          <ManualAddress
            metaData={metaData}
            productType={productType}
            onFieldChange={onFieldChange}
            showManualAddress={showManualAddress}
            asyncFetching={spinner}
            asyncSuccess={isValid}
            setAsyncFetching={setAsyncFetching}
            graphQuery={graphQuery}
            graphError={graphError}
            graphMessage={graphMessage}
            onGraphError={onGraphError}
            disqualificationMessage={disqualificationMessage}
            setDisqualification={setDisqualification}
            recordDisqualification={recordDisqualification}
          />
        </QASAddressLookUp>
      </QualifyingField>
    );
  }
}

export { AddressField as default };
